import React, { lazy, Suspense } from 'react';
import RoleRoute from './components/Auth/RoleRoute';
const ContentTasks = lazy(() => import('./components/ContentTasks/ContentTasks'));
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
const Layout = lazy(() => import('./components/Layout/Layout'));
const HealthDashboard = lazy(() => import('./components/Health/HealthDashboard'));
const TenantManagement = lazy(() => import('./components/Tenants/TenantManagement'));
const InstitutionSetup = lazy(() => import('./components/Tenants/InstitutionSetup'));
const AdminManagement = lazy(() => import('./components/Admins/AdminManagement'));
const AnalystManagement = lazy(() => import('./components/Analysts/AnalystManagement'));
const SuperAdminManagement = lazy(() => import('./components/Admins/SuperAdminManagement'));
const QuestionManagement = lazy(() => import('./components/Questions/QuestionManagement'));
const DraftQuestionManagement = lazy(() => import('./components/DraftQuestions/DraftQuestionManagement'));
const UserManagement = lazy(() => import('./components/users/UserManagement'));
const InstitutionAccess = lazy(() => import('./components/users/InstitutionAccess'));
const CreateUserForm = lazy(() => import('./components/users/CreateUserForm'));
const RoleBasedDashboard = lazy(() => import('./components/RoleBasedDashboard'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const Login = lazy(() => import('./components/Auth/Login'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AssignedWork = lazy(() => import('./components/ContentGenerator/AssignedWork'));
const MyQuestions = lazy(() => import('./components/ContentGenerator/MyQuestions'));
const Tags = lazy(() => import('./components/ContentGenerator/Tags'));
const CreateQuestion = lazy(() => import('./components/ContentGenerator/CreateQuestion'));
const BulkUpload = lazy(() => import('./components/ContentGenerator/BulkUpload'));
const KitManagement = lazy(() => import('./components/KitManagement/KitManagement'));
const WorkflowManagement = lazy(() => import('./components/Workflow/WorkflowManagement'));
const Analytics = lazy(() => import('./components/Analytics/Analytics'));
const QuestionsForReview = lazy(() => import('./components/ContentReviewer/QuestionsForReview'));
const AssignedQuestions = lazy(() => import('./components/ContentReviewer/AssignedQuestions'));
const CompletedReviews = lazy(() => import('./components/ContentReviewer/CompletedReviews'));
const UUIDHelper = lazy(() => import('./components/UUIDHelper/UUIDHelper'));
const TestConfigurationsPage = lazy(() => import('./pages/TestConfigurationsPage'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, sessionError, retrySession } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (sessionError) return <main className="mx-auto max-w-xl p-6"><h1 className="page-heading">Session verification unavailable</h1><p role="alert" className="mt-3">{sessionError}</p><button type="button" className="btn-primary mt-4" onClick={retrySession}>Retry session verification</button></main>;
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// App Routes Component
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Suspense fallback={<div role="status" className="p-6">Loading page…</div>}>
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<RoleRoute path="dashboard"><RoleBasedDashboard /></RoleRoute>} />
        <Route path="health" element={<RoleRoute path="health"><HealthDashboard /></RoleRoute>} />
        <Route path="tenants" element={<RoleRoute path="tenants"><TenantManagement /></RoleRoute>} />
        <Route path="tenants/setup" element={<RoleRoute path="tenants/setup"><InstitutionSetup /></RoleRoute>} />
        <Route path="admins" element={<RoleRoute path="admins"><AdminManagement /></RoleRoute>} />
        <Route path="analysts" element={<RoleRoute path="analysts"><AnalystManagement /></RoleRoute>} />
        <Route path="super-admins" element={<RoleRoute path="super-admins"><SuperAdminManagement /></RoleRoute>} />
        <Route path="institution-access" element={<RoleRoute path="institution-access"><InstitutionAccess /></RoleRoute>} />
        <Route path="users" element={<RoleRoute path="users"><UserManagement /></RoleRoute>} />
        <Route path="users/create" element={<RoleRoute path="users/create"><CreateUserForm /></RoleRoute>} />
        <Route path="questions" element={<RoleRoute path="questions"><ContentTasks queue="library" /></RoleRoute>} />
        <Route path="questions/create" element={<RoleRoute path="questions/create"><CreateQuestion /></RoleRoute>} />
        <Route path="questions/bulk-upload" element={<RoleRoute path="questions/bulk-upload"><BulkUpload /></RoleRoute>} />
        <Route path="assigned-work" element={<RoleRoute path="assigned-work"><ContentTasks queue="author-assignments" /></RoleRoute>} />
        <Route path="my-questions" element={<RoleRoute path="my-questions"><ContentTasks queue="drafts" /></RoleRoute>} />
        <Route path="tags" element={<RoleRoute path="tags"><Tags /></RoleRoute>} />
        <Route path="uuid-helper" element={<RoleRoute path="uuid-helper"><UUIDHelper /></RoleRoute>} />
        <Route path="kits" element={<RoleRoute path="kits"><KitManagement /></RoleRoute>} />
        <Route path="publishing" element={<RoleRoute path="publishing"><ContentTasks queue="publishing" /></RoleRoute>} />
        <Route path="workflow" element={<RoleRoute path="workflow"><ContentTasks queue="operations" /></RoleRoute>} />
        <Route path="team" element={<RoleRoute path="team"><Navigate to="/users" replace /></RoleRoute>} />
        <Route path="analytics" element={<RoleRoute path="analytics"><Analytics /></RoleRoute>} />
        <Route path="questions-for-review" element={<RoleRoute path="questions-for-review"><ContentTasks queue="reviews" /></RoleRoute>} />
        <Route path="assigned-questions" element={<RoleRoute path="assigned-questions"><ContentTasks queue="reviews" /></RoleRoute>} />
        <Route path="completed-reviews" element={<RoleRoute path="completed-reviews"><ContentTasks queue="decisions" /></RoleRoute>} />
        <Route path="draft-questions" element={<RoleRoute path="draft-questions"><ContentTasks queue="operations" /></RoleRoute>} />
        <Route path="test-configurations" element={<RoleRoute path="test-configurations"><TestConfigurationsPage /></RoleRoute>} />
        <Route path="settings" element={<RoleRoute path="settings"><Settings /></RoleRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-slate-50">
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#363636',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e2e8f0',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
